import { ReceivableResponse } from '@efex-services/receivables/types';
import useBoolean from '@main-hooks/useBoolean';
import theme from '@efexteam/mui-theme';
import {
  Tab,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  useMediaQuery,
} from '@mui/material';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomDrawer from '../../components/common/Drawer';
import Payment from '../../pages/payment/Payment';
import TableRows from './ReceivablesTable/TableRows';
import RECEIVABLES_TABS from './constants/ReceivablesTable.constants';
import useTableController from './hooks/useTableController';
import RECEIVABLES_TABS_NUMBER from './constants/ReceivableTabs.constants';
import CollectLocalPay from './components/collectPayLocalCurrencie/CollectLocalPay';
import ReceivablesTableStyles from './ReceivablesTable.style';

interface ReceivablesTableProps {
  receivables: ReceivableResponse[];
  initalTab?: number;
  setMovementType: (type: keyof typeof RECEIVABLES_TABS) => void;
}

const ReceivablesTable = ({
  receivables,
  initalTab = RECEIVABLES_TABS_NUMBER.MOVEMENTS,
  setMovementType,
}: ReceivablesTableProps) => {
  const [componentSelectedTab, setComponentSelectedTab] = useState(initalTab);
  const {
    tableConvertData,
    selectedTab,
    tableHeaders,
    tableType,
    handleChange,
  } = useTableController({ initialTab: initalTab });
  const { t } = useTranslation();
  const [invoiceData, setInvoiceData] = useState<any>();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [isDrawerOpened, { setFalse: hideDrawer, setTrue: showDrawer }] =
    useBoolean(false);

  const showInvoice = (uuid: string, paymentId: number) => {
    setInvoiceData({
      origin: 'receivable',
      identify: uuid,
      paymentId,
    });
    showDrawer();
  };

  const rowsData = useMemo(
    () =>
      tableType === 'MOVEMENTS'
        ? tableConvertData.convertData(receivables)
        : [],
    [tableConvertData, tableType, receivables]
  );

  useEffect(() => {
    if (componentSelectedTab !== selectedTab) {
      handleChange(componentSelectedTab);
      setMovementType(
        Object.keys(RECEIVABLES_TABS)[componentSelectedTab] as any
      );
    }
  }, [componentSelectedTab, handleChange, selectedTab, setMovementType]);

  // console.log(selectedTab);

  return (
    <>
      <Tabs
        value={selectedTab}
        onChange={(_, value) => setComponentSelectedTab(value)}
        aria-label="basic tabs example"
        className="mb-4"
      >
        {Object.keys(RECEIVABLES_TABS).map((tab) => (
          <Tab key={tab} label={t(tab)} />
        ))}
      </Tabs>
      <TableContainer className={ReceivablesTableStyles.tableContainer}>
        <Table className={clsx('mui-table', ReceivablesTableStyles.table)}>
          {selectedTab === RECEIVABLES_TABS_NUMBER.COLLECT_LOCAL_PAYMENT ? (
            <CollectLocalPay />
          ) : null}
          {selectedTab !== RECEIVABLES_TABS_NUMBER.COLLECT_LOCAL_PAYMENT ? (
            <>
              <TableHead className={ReceivablesTableStyles.tableHead}>
                <TableRow>
                  {tableHeaders.map((header, index) => (
                    <TableCell
                      key={header}
                      className={ReceivablesTableStyles.tableHeaderCell(
                        index === 0 ? !isMobile : false
                      )}
                    >
                      <Typography variant="caption">{t(header)}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableRows
                receivables={receivables}
                rowsData={rowsData}
                showInvoice={showInvoice}
                isMobile={isMobile}
              />
            </>
          ) : null}
        </Table>
      </TableContainer>
      {isDrawerOpened && (
        <CustomDrawer
          PaperProps={{ id: 'paymentWrapper' }}
          position="right"
          isOpen={isDrawerOpened}
          onClose={hideDrawer}
        >
          <Payment
            onClose={hideDrawer}
            identify={invoiceData.identify}
            origin={invoiceData.origin}
            paymentId={invoiceData.paymentId}
          />
        </CustomDrawer>
      )}
    </>
  );
};

export default ReceivablesTable;
